import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFire,
  faClock,
  faChevronRight,
  faBolt,
  faCheck,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

/**
 * GlobalFightCard - Mockup-style fight card display for the Global Competition page.
 * Shows fights in a visual card layout with fighter matchups, status badges, and results.
 */
const GlobalFightCard = ({ fights, eventName, eventDateTime, eventId }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  if (!fights || fights.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon={faFire} className="text-gray-400 w-7 h-7" />
        </div>
        <h3 className="text-lg font-bold text-dark-grey mb-2">
          No Fights Available
        </h3>
        <p className="text-medium-grey text-sm">
          Fight card information will be available soon.
        </p>
      </div>
    );
  }

  // Separate active and cancelled fights
  const activeFights = fights.filter((fight) => !fight.hidden);
  const sortedFights = [...activeFights].sort((a, b) => {
    if (a.IsMainEvent && !b.IsMainEvent) return -1;
    if (!a.IsMainEvent && b.IsMainEvent) return 1;
    return (a.Order || 0) - (b.Order || 0);
  });

  const displayedFights = showAll ? sortedFights : sortedFights.slice(0, 4);
  const hasMore = sortedFights.length > 4;

  // Check if event has started
  const hasEventStarted = eventDateTime
    ? new Date(eventDateTime) <= new Date()
    : false;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-lg text-dark-grey">Fight Card</h2>
        <span className="text-xs text-medium-grey">
          {sortedFights.length} fights
        </span>
      </div>

      {/* Fight Cards */}
      {displayedFights.map((fight, index) => (
        <FightCardItem
          key={fight._id || index}
          fight={fight}
          hasEventStarted={hasEventStarted}
          onViewEvent={() => eventId && navigate(`/events/${eventId}`)}
        />
      ))}

      {/* View All / Collapse */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full py-3 text-sm text-medium-grey hover:text-dark-grey transition-colors flex items-center justify-center gap-1"
        >
          {showAll ? "Show Less" : `View All ${sortedFights.length} Fights`}
          <FontAwesomeIcon
            icon={faChevronRight}
            className={`w-3 h-3 transition-transform ${showAll ? "rotate-90" : ""}`}
          />
        </button>
      )}
    </div>
  );
};

/**
 * Individual fight card matching the mockup design
 */
const FightCardItem = ({ fight, hasEventStarted, onViewEvent }) => {
  const fighter1 = fight.Fighters?.[0];
  const fighter2 = fight.Fighters?.[1];

  if (!fighter1 || !fighter2) return null;

  // Determine fight status
  const fighter1Outcome = fighter1.Outcome?.toLowerCase() || "";
  const fighter2Outcome = fighter2.Outcome?.toLowerCase() || "";
  const isComplete =
    fighter1Outcome.includes("win") ||
    fighter1Outcome.includes("loss") ||
    fighter2Outcome.includes("win") ||
    fighter2Outcome.includes("loss");
  const isLive = hasEventStarted && !isComplete;

  const fighter1Won = fighter1Outcome.includes("win");
  const fighter2Won = fighter2Outcome.includes("win");

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Fight Header */}
      <div className="px-4 py-2 bg-light-grey flex items-center justify-between">
        <div className="flex items-center gap-2">
          {fight.IsMainEvent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-deep-red/10 text-deep-red">
              <FontAwesomeIcon icon={faFire} className="w-2.5 h-2.5" />
              Main Event
            </span>
          )}
          <span className="text-xs font-medium text-medium-grey">
            {fight.WeightClass || ""}
          </span>
        </div>
        {isLive ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-deep-red/10 text-deep-red border border-deep-red/20">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
            </span>
            LIVE
          </span>
        ) : isComplete ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-medium-grey">
            Final
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-medium-grey">
            <FontAwesomeIcon icon={faClock} className="w-2.5 h-2.5" />
            Upcoming
          </span>
        )}
      </div>

      {/* Fighters */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Fighter 1 */}
          <FighterDisplay fighter={fighter1} isWinner={fighter1Won} />

          {/* VS */}
          <div className="px-3 flex flex-col items-center">
            <span className="text-xl font-bold text-gray-300">VS</span>
          </div>

          {/* Fighter 2 */}
          <FighterDisplay fighter={fighter2} isWinner={fighter2Won} />
        </div>

        {/* Action Button */}
        <button
          onClick={onViewEvent}
          className="w-full mt-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-dark-grey transition-colors flex items-center justify-center gap-2"
        >
          {isComplete ? "View Details" : "View Event"}
          <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

/**
 * Individual fighter display within a fight card
 */
const FighterDisplay = ({ fighter, isWinner }) => {
  const firstName = fighter.FirstName || "";
  const lastName = fighter.LastName || "";

  return (
    <div className="flex-1 flex flex-col items-center text-center">
      {/* Avatar */}
      <div className="relative mb-2">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
          <FontAwesomeIcon icon={faUser} className="w-6 h-6 text-gray-400" />
        </div>
        {isWinner && (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Name */}
      <h3 className="font-semibold text-sm leading-tight text-dark-grey">
        {firstName}
      </h3>
      <h3 className="font-semibold text-sm leading-tight text-dark-grey mb-1">
        {lastName}
      </h3>

      {/* Result badge */}
      {isWinner && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
          <FontAwesomeIcon icon={faCheck} className="w-2 h-2" />
          WIN
        </span>
      )}
      {fighter.Outcome?.toLowerCase().includes("loss") && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
          LOSS
        </span>
      )}
    </div>
  );
};

export default GlobalFightCard;
